import {
  DATE_FORMAT,
  DATE_WITH_SHORT_TIME_FORMAT,
  formatDate,
} from '@utils/date';
import React, { FC, useEffect, useState } from 'react';
import { I18n, Translate } from 'react-redux-i18n';
import { Link } from 'react-router';
import { useRedux } from 'use-redux';
import { getPayments as actionGetPayments } from '../../actions/user';

import ReactTable from 'react-table';
import 'react-table/react-table.css';

import Row from 'react-bootstrap/lib/Row';
import TranslatedText from '../../i18n/TranslatedText';

import { isSubsValid } from '../../actions/utils';
import { STATUSES } from '../gui/admin/stats/PaymentStats';
import { PaymentApi } from '@api/payment';
import { SubscriptionApi } from '@api/subscription';

const findPlan = (plans, name) => plans.find(p => p.name === name);

const PaymentHistory: FC<void> = () => {
  const [subscriptions, setSubscriptions] = useState([] as any[]);
  const [
    {
      auth: { user, payments = [], plans = [] },
    },
    dispatch,
  ] = useRedux();

  useEffect(() => {
    dispatch(actionGetPayments());

    SubscriptionApi.listUserSubscriptions().then(res => {
      setSubscriptions(res);
    });
  }, [dispatch]);

  const columns = [
    {
      Header: I18n.t('userSubscribition.paymentHistory.dateOfPayment'),
      accessor: 'created',
      className: 'text-center',
      Cell: props => (
        <span> {formatDate(props.value, DATE_WITH_SHORT_TIME_FORMAT)} </span>
      ),
    },
    {
      Header: I18n.t('userSubscribition.paymentHistory.plan'),
      accessor: d => findPlan(plans, d.plan),
      id: 'plan',
      className: 'text-center',
      Cell: props => (
        <span>
          {props.value ? (
            <TranslatedText text={props.value.title} />
          ) : (
            I18n.t('other.unknown')
          )}
        </span>
      ),
    },
    {
      Header: I18n.t('userSubscribition.paymentHistory.price'),
      accessor: 'price',
      className: 'text-center',
    },
    {
      Header: I18n.t('userSubscribition.paymentHistory.withTeacher'),
      accessor: d => findPlan(plans, d.plan),
      id: '_withTeacher',
      className: 'text-center',
      Cell: props => (
        <span>
          {props.value
            ? props.value.teacher
              ? I18n.t('other.yes')
              : I18n.t('other.no')
            : I18n.t('other.unknown')}
        </span>
      ),
    },
    {
      Header: I18n.t('userSubscribition.paymentHistory.duration'),
      accessor: 'amount',
    },
    {
      Header: I18n.t('userSubscribition.paymentHistory.promocode'),
      accessor: 'promocode',
      className: 'text-center',
    },
    {
      Header: I18n.t('userSubscribition.paymentHistory.status'),
      accessor: 'status',
      Cell: props => {
        const status = STATUSES.find(s => s.elements.includes(props.value));
        return <span> {status ? status.text : props.value} </span>;
      },
    },
  ];

  const cancelSubscription = (subId: string) => {
    SubscriptionApi.cancelSubscription(subId).then(res => {
      setSubscriptions(
        subscriptions.map(item =>
          item._id === subId ? { ...item, status: 'canceled' } : item
        )
      );
    });
  };

  const subscriptionsColumns = [
    {
      Header: I18n.t('userSubscribition.subscriptionsHistory.nextChargeAt'),
      accessor: 'nextChargeAt',
      className: 'text-center',
      Cell: props =>
        props.value
          ? formatDate(props.value, DATE_WITH_SHORT_TIME_FORMAT)
          : '-',
    },
    {
      Header: I18n.t('userSubscribition.subscriptionsHistory.lastChargedAt'),
      accessor: 'lastChargedAt',
      className: 'text-center',
      Cell: props =>
        props.value
          ? formatDate(props.value, DATE_WITH_SHORT_TIME_FORMAT)
          : '-',
    },
    {
      Header: I18n.t('userSubscribition.paymentHistory.status'),
      accessor: 'status',
      Cell: props => {
        const status = STATUSES.find(s => s.elements.includes(props.value));
        return <span> {status ? status.text : props.value} </span>;
      },
    },
    {
      Header: I18n.t('userSubscribition.subscriptionsHistory.lessonsCount'),
      accessor: 'lessonsCount',
    },
    {
      accessor: '_id',
      className: 'text-center',
      Cell: props => {
        return props.original.status === 'active' ? (
          <a href="#" onClick={() => cancelSubscription(props.value)}>
            {I18n.t('userSubscribition.cancel')}
          </a>
        ) : (
          ''
        );
      },
    },
  ];

  const completedStripePayments: string[] = payments
    .filter(p => p.method === 'stripe' && p.status === 'COMPLETED')
    .map(p => p._id);

  completedStripePayments.length &&
    columns.push({
      Header: I18n.t('userSubscribition.paymentHistory.receipt'),
      accessor: '_id',
      className: 'text-center',
      Cell: props => {
        const loadReceipt = async () => {
          try {
            const url = await PaymentApi.getReceiptUrl(props.value);
            if (url) window.open(url, '_blank');
          } catch (error) {}
        };
        const hasReceipt = completedStripePayments.includes(props.value);

        return hasReceipt ? (
          <a href="#" onClick={loadReceipt}>
            {I18n.t('userSubscribition.download')}
          </a>
        ) : (
          <></>
        );
      },
    });

  const validDate = new Date(user.subs.valid);

  const currentPlan = findPlan(plans, user.subs.plan);
  return (
    <div className="container-fluid new-style code-gray-bg">
      <Row className="md-container">
        {isSubsValid(user) ? (
          <div className="sub-title">
            {currentPlan && !currentPlan.teacher ? (
              <span className="text-danger">
                {I18n.t('userSubscribition.subscriptionExpires', {
                  plan: currentPlan && (
                    <TranslatedText text={currentPlan.title} />
                  ),
                  date: formatDate(validDate, DATE_FORMAT),
                })}
              </span>
            ) : (
              <span className="text-danger">
                {I18n.t('userSubscribition.titleWithTeacher')}{' '}
                {user.subs.lessons}.
              </span>
            )}

            <br />
            <Link to="/i/plan">{I18n.t('userSubscribition.extendButton')}</Link>
          </div>
        ) : (
          <div className="sub-title">
            {<Translate value="userSubscribition.noSubs" />}
            <br />
            <Link to="/i/plan">{I18n.t('userSubscribition.goToPlans')}</Link>
          </div>
        )}

        <div className="container-fluid">
          <h2 className="title">
            <div className="quote" />
            <Translate value="userSubscribition.subscriptionsHistory.title" />
          </h2>

          <ReactTable
            data={subscriptions}
            columns={subscriptionsColumns}
            className="bg-white"
            defaultPageSize={5}
            showPageSizeOptions={false}
            defaultSorted={[
              {
                id: 'created',
                desc: true,
              },
            ]}
            getTrProps={(state, rowInfo, column) => {
              const status = rowInfo && rowInfo.row.status;
              const current = STATUSES.find(s => s.elements.includes(status));

              return {
                className: current ? current.className : '',
              };
            }}
          />
        </div>

        <div className="container-fluid">
          <h2 className="title">
            <div className="quote" />
            <Translate value="userSubscribition.paymentHistory.title" />
          </h2>

          <ReactTable
            data={payments}
            columns={columns}
            className="bg-white"
            defaultPageSize={10}
            showPageSizeOptions={false}
            defaultSorted={[
              {
                id: 'created',
                desc: true,
              },
            ]}
            getTrProps={(state, rowInfo, column) => {
              const status = rowInfo && rowInfo.row.status;
              const current = STATUSES.find(s => s.elements.includes(status));

              return {
                className: current ? current.className : '',
              };
            }}
          />
        </div>
      </Row>
    </div>
  );
};

export default PaymentHistory;
